import { useEffect, useMemo, useState } from "react";
import { useCastle } from "../api/castle";
import {
  HEAL_COST_RATIO,
  REVIVE_COST_RATIO,
  useMe,
  useStartHeal,
  useStartPromotion,
  useStartRevive,
  useStartTraining,
  useUnitCatalog,
} from "../api/army";
import { Loading } from "./Loading";
import { ErrorView } from "./ErrorView";
import { ResourceBar } from "../components/ResourceBar";
import { ScreenBanner } from "../components/ScreenBanner";
import { RecruitModal } from "../components/RecruitModal";
import { PromoteModal } from "../components/PromoteModal";
import { HeroesScreen } from "./HeroesScreen";
import { useNow } from "../lib/ticker";
import { formatDuration, formatNumber, secondsUntil } from "../lib/format";
import {
  ElementRu,
  UnitCatalogEntry,
  UnitClassRu,
} from "../types/army";
import { GiAnkh, GiHealthPotion } from "react-icons/gi";
import { CurrencyValue } from "../components/CurrencyIcon";
import { UnitIcon } from "../components/UnitIcon";
import { TrainingSpeedupModal } from "../components/TrainingSpeedupModal";
import type { TrainingOrder } from "../types/castle";
import { maxAllowedTier, parseGarrisonKey, tierCostMultiplier, tierStatMultiplier } from "../lib/garrisonKey";

export function ArmyScreen() {
  const castle = useCastle();
  const me = useMe();
  const units = useUnitCatalog();
  const train = useStartTraining();
  const promote = useStartPromotion();
  const startHeal = useStartHeal();
  const startRevive = useStartRevive();
  const now = useNow(1000);

  const [recruiting, setRecruiting] = useState<UnitCatalogEntry | null>(null);
  const [promoting, setPromoting] = useState<
    { unit: UnitCatalogEntry; fromTier: number; available: number } | null
  >(null);
  const [speedupTarget, setSpeedupTarget] = useState<TrainingOrder | null>(null);
  const [sub, setSub] = useState<"units" | "heroes">("units");

  // Heroes sub-tab is gated on actually having a Hero Hall built. Without
  // it the player can't recruit / level heroes, so the tab is just a dead
  // screen with a "build the Hall" hint — hide entirely until the player
  // builds one (HeroHall = BuildingType 13).
  const hasHeroHall = useMemo(() => {
    if (!castle.data) return false;
    return (
      castle.data.state.buildings.find((b) => b.type === 13)?.level ?? 0
    ) >= 1;
  }, [castle.data]);

  // If the player was sitting on the Heroes sub-tab and the Hall isn't
  // built (or was demolished), snap back to Units. Cheap effect — only
  // fires when hasHeroHall flips.
  useEffect(() => {
    if (!hasHeroHall && sub === "heroes") setSub("units");
  }, [hasHeroHall, sub]);

  // Unit class → BuildingType that's required to train it. Mirrors
  // CastleService.BuildingForClass on the server: Infantry→Barracks,
  // Ranger→ArcheryRange, Mage→MageTower, Signature→SignatureForge.
  // Hiding unavailable units keeps the recruit list clean for newbies —
  // when they build a new training structure, the matching unit appears.
  const buildingForClass: Record<number, number> = {
    0: 6, // Barracks
    1: 7, // ArcheryRange
    2: 8, // MageTower
    3: 9, // SignatureForge
  };

  const builtClasses = useMemo(() => {
    const out = new Set<number>();
    if (!castle.data) return out;
    for (const b of castle.data.state.buildings) {
      if (b.level <= 0) continue;
      for (const [cls, type] of Object.entries(buildingForClass)) {
        if (b.type === type) out.add(Number(cls));
      }
    }
    return out;
  }, [castle.data]);

  const myUnits = useMemo(() => {
    if (!units.data || !me.data) return [];
    return units.data
      .filter((u) => u.trainable && u.element === me.data!.element)
      .filter((u) => builtClasses.has(u.cls))
      .sort((a, b) => a.cls - b.cls);
  }, [units.data, me.data, builtClasses]);

  const unitsById = useMemo(() => {
    const m = new Map<string, UnitCatalogEntry>();
    units.data?.forEach((u) => m.set(u.id, u));
    return m;
  }, [units.data]);

  // Data lookups intentionally accept undefined (when castle.data hasn't
  // loaded yet) so the hooks below can run unconditionally. React's Rules
  // of Hooks require every hook to be called on every render in the same
  // order — moving these `useMemo`s after the early `Loading` return makes
  // hook count vary and crashes the page with a black screen on first nav.
  const trainingOrders = castle.data?.state.trainingOrders ?? [];
  const hospital = castle.data?.state.hospital ?? [];
  const temple = castle.data?.state.temple ?? [];
  const hospitalPool = castle.data?.state.hospitalPool ?? {};
  const templePool = castle.data?.state.templePool ?? {};

  // Per-unit active orders. If the player has multiple batches in flight for
  // the same unitId (e.g. two queued trainings of Лавовый страж), we show
  // the one with the soonest finish — that's the next user-relevant event.
  const activeTrainByUnit = useMemo(() => {
    const m = new Map<string, TrainingOrder>();
    for (const o of trainingOrders) {
      const cur = m.get(o.unitId);
      if (!cur || new Date(o.finishesAt) < new Date(cur.finishesAt)) m.set(o.unitId, o);
    }
    return m;
  }, [trainingOrders]);

  // Maps key on the full GarrisonKey ("unitId@tier") so two batches of the
  // same unit at different tiers don't collide.
  const activeHealByKey = useMemo(() => {
    const m = new Map<string, typeof hospital[number]>();
    for (const o of hospital) {
      const t = o.tier ?? 1;
      const k = t > 1 ? `${o.unitId}@${t}` : o.unitId;
      const cur = m.get(k);
      if (!cur || new Date(o.finishesAt) < new Date(cur.finishesAt)) m.set(k, o);
    }
    return m;
  }, [hospital]);

  const activeReviveByKey = useMemo(() => {
    const m = new Map<string, typeof temple[number]>();
    for (const o of temple) {
      const t = o.tier ?? 1;
      const k = t > 1 ? `${o.unitId}@${t}` : o.unitId;
      const cur = m.get(k);
      if (!cur || new Date(o.finishesAt) < new Date(cur.finishesAt)) m.set(k, o);
    }
    return m;
  }, [temple]);

  // Union of (unitId, tier) keys that should render in each section: pool
  // entry > 0 OR an active healing/revival batch in flight. Sorted by unit
  // class, then by tier ascending.
  const hospitalKeys = useMemo(() => {
    const s = new Set<string>([...Object.keys(hospitalPool), ...activeHealByKey.keys()]);
    return Array.from(s)
      .filter((k) => (hospitalPool[k] ?? 0) > 0 || activeHealByKey.has(k))
      .sort((a, b) => {
        const pa = parseGarrisonKey(a);
        const pb = parseGarrisonKey(b);
        const ca = unitsById.get(pa.unitId)?.cls ?? 99;
        const cb = unitsById.get(pb.unitId)?.cls ?? 99;
        if (ca !== cb) return ca - cb;
        if (pa.unitId !== pb.unitId) return pa.unitId.localeCompare(pb.unitId);
        return pa.tier - pb.tier;
      });
  }, [hospitalPool, activeHealByKey, unitsById]);

  const templeKeys = useMemo(() => {
    const s = new Set<string>([...Object.keys(templePool), ...activeReviveByKey.keys()]);
    return Array.from(s)
      .filter((k) => (templePool[k] ?? 0) > 0 || activeReviveByKey.has(k))
      .sort((a, b) => {
        const pa = parseGarrisonKey(a);
        const pb = parseGarrisonKey(b);
        const ca = unitsById.get(pa.unitId)?.cls ?? 99;
        const cb = unitsById.get(pb.unitId)?.cls ?? 99;
        if (ca !== cb) return ca - cb;
        if (pa.unitId !== pb.unitId) return pa.unitId.localeCompare(pb.unitId);
        return pa.tier - pb.tier;
      });
  }, [templePool, activeReviveByKey, unitsById]);

  if (castle.isPending || me.isPending || units.isPending) {
    return <Loading message="Армия строится..." />;
  }
  if (castle.isError || me.isError || units.isError) {
    return <ErrorView message="Не получилось загрузить армию." />;
  }

  const data = castle.data!;
  const garrison = data.state.garrison;
  const trainSpeed = data.researchBonuses.trainSpeed ?? 0;
  const promotionOrders = data.state.promotionOrders ?? [];

  // Level of the training building for a unit class (0 if not built).
  const buildingLevelForClass = (cls: number): number => {
    const bt = buildingForClass[cls];
    return bt != null ? (data.state.buildings.find((b) => b.type === bt)?.level ?? 0) : 0;
  };

  // A class's building is busy if it has an active training OR promotion. While
  // busy we hide the «Повысить» action so the player isn't offered an action
  // the server will reject with BuildingBusy.
  const busyClasses = new Set<number>();
  for (const o of trainingOrders) {
    const c = unitsById.get(o.unitId)?.cls;
    if (c != null) busyClasses.add(c);
  }
  for (const o of promotionOrders) {
    const c = unitsById.get(o.unitId)?.cls;
    if (c != null) busyClasses.add(c);
  }

  const promoteError =
    promote.error &&
    typeof promote.error === "object" &&
    "response" in promote.error &&
    promote.error.response &&
    typeof promote.error.response === "object" &&
    "data" in promote.error.response
      ? (promote.error.response as { data?: { error?: string } }).data?.error ?? null
      : null;

  const errorMessage =
    train.error &&
    typeof train.error === "object" &&
    "response" in train.error &&
    train.error.response &&
    typeof train.error.response === "object" &&
    "data" in train.error.response
      ? (train.error.response as { data?: { error?: string } }).data?.error ?? null
      : null;

  return (
    <div className="flex h-full flex-col">
      <ScreenBanner
        kind="army"
        title="Армия"
        subtitle={`${ElementRu[me.data!.element]} · Сила ${formatNumber(data.powerBreakdown?.total ?? data.armyPower)}`}
      />
      <ResourceBar resources={data.state.resources} safeStorage={data.safeStorage} />

      {hasHeroHall && (
        <div className="grid grid-cols-2 gap-1 px-2 pt-2">
          <button
            type="button"
            onClick={() => setSub("units")}
            className={`rounded-lg py-2 text-sm ${
              sub === "units"
                ? "bg-tg-button text-tg-buttonText"
                : "bg-tg-secondaryBg text-tg-hint"
            }`}
          >
            Войска
          </button>
          <button
            type="button"
            onClick={() => setSub("heroes")}
            className={`rounded-lg py-2 text-sm ${
              sub === "heroes"
                ? "bg-tg-button text-tg-buttonText"
                : "bg-tg-secondaryBg text-tg-hint"
            }`}
          >
            Герои
          </button>
        </div>
      )}

      {sub === "heroes" ? (
        <div className="mt-2 flex-1 overflow-hidden">
          <HeroesScreen />
        </div>
      ) : (
      <>
      <div className="flex items-baseline justify-between px-4 py-2 text-sm">
        <div>
          <span className="text-tg-hint">Стихия: </span>
          <span className="font-medium">{ElementRu[me.data!.element]}</span>
        </div>
        <div className="text-tg-hint">
          Сила {(data.powerBreakdown?.total ?? data.armyPower).toLocaleString("ru-RU")}
        </div>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto px-3 pb-3">
        <Section title="Гарнизон">
          {Object.keys(garrison).length === 0 ? (
            <Empty text="Гарнизон пуст. Найми хотя бы одного юнита ниже." />
          ) : (
            <div className="space-y-1">
              {(() => {
                // Decode every cell into (unitId, tier, count), sort by class
                // then tier so the front line settles ahead of the strongest
                // ranks within the same unit family.
                const stacks = Object.entries(garrison).map(([key, count]) => {
                  const { unitId, tier } = parseGarrisonKey(key);
                  return { key, unitId, tier, count, unit: unitsById.get(unitId) };
                });
                stacks.sort((a, b) => {
                  const ca = a.unit?.cls ?? 99;
                  const cb = b.unit?.cls ?? 99;
                  if (ca !== cb) return ca - cb;
                  if (a.unitId !== b.unitId) return a.unitId.localeCompare(b.unitId);
                  return a.tier - b.tier;
                });
                return stacks.map(({ key, unitId, tier, count, unit: u }) => {
                  const mult = tierStatMultiplier(tier);
                  const hp = u ? Math.round(u.hp * mult) : 0;
                  const atk = u ? Math.round(u.attack * mult) : 0;
                  const def = u ? Math.round(u.defense * mult) : 0;
                  const cls = u?.cls;
                  // Eligible to promote: building can reach a higher tier and
                  // isn't already busy with a training/promotion order.
                  const canPromote =
                    u != null &&
                    cls != null &&
                    count > 0 &&
                    tier < maxAllowedTier(buildingLevelForClass(cls)) &&
                    !busyClasses.has(cls);
                  return (
                    <Row
                      key={key}
                      icon={<UnitIcon unitId={unitId} cls={u?.cls} />}
                      title={u?.name ?? unitId}
                      tierBadge={`T${tier}`}
                      subtitle={
                        u
                          ? `${UnitClassRu[u.cls]} · Hp ${hp} · Atk ${atk} · Def ${def}`
                          : ""
                      }
                      right={formatNumber(count)}
                      action={
                        canPromote ? (
                          <button
                            type="button"
                            onClick={() => setPromoting({ unit: u!, fromTier: tier, available: count })}
                            className="rounded-lg bg-tg-bg px-2 py-1 text-xs text-tg-accent active:opacity-70"
                            title="Повысить тир"
                          >
                            ↑ Повысить
                          </button>
                        ) : null
                      }
                    />
                  );
                });
              })()}
            </div>
          )}
        </Section>

        {promotionOrders.length > 0 && (
          <Section title="Повышение">
            <div className="space-y-2">
              {promotionOrders.map((o, i) => {
                const u = unitsById.get(o.unitId);
                const remaining = secondsUntil(o.finishesAt, now);
                return (
                  <div key={`${o.unitId}-${i}`} className="flex items-center gap-3 rounded-xl bg-tg-secondaryBg p-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-tg-bg">
                      <UnitIcon unitId={o.unitId} cls={u?.cls} size={26} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="truncate text-sm font-medium">{u?.name ?? o.unitId}</div>
                        <span className="rounded bg-tg-bg px-1.5 py-0.5 text-[10px] tabular-nums text-tg-accent">
                          T{o.fromTier} → T{o.toTier}
                        </span>
                      </div>
                      <div className="mt-0.5 text-xs text-tg-hint">× {formatNumber(o.count)}</div>
                    </div>
                    <div className="text-sm tabular-nums text-tg-text">{formatDuration(remaining)}</div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {/* Unified "Найм" — each row swaps between hire UI and active training
            progress. When a unit is being trained, the recruit button is
            replaced by a progress card that opens TrainingSpeedupModal on tap.
            Multi-batch case: the earliest-finishing batch shows; rest are
            reachable via the speedup modal too. */}
        <Section title="Найм" collapsible storageKey="recruit">
          {myUnits.length === 0 ? (
            <Empty text="Чтобы нанимать войска — построй здание тренировки. Пехота открывается в Казармах, лучники в Стрельбище, маги в Магической башне." />
          ) : (
          <div className="space-y-2">
            {myUnits.map((u) => {
              const active = activeTrainByUnit.get(u.id);
              if (active) {
                const remaining = secondsUntil(active.finishesAt, now);
                const extraBatches = trainingOrders.filter((o) => o.unitId === u.id).length - 1;
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => setSpeedupTarget(active)}
                    className="flex w-full items-center gap-3 rounded-xl bg-tg-secondaryBg p-3 text-left active:bg-tg-bg"
                  >
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-tg-bg">
                      <UnitIcon unitId={u.id} cls={u.cls} size={28} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="truncate font-medium">{u.name}</div>
                        <div className="rounded bg-tg-bg px-1.5 py-0.5 text-xs text-tg-accent">
                          Тренируется
                        </div>
                      </div>
                      <div className="mt-0.5 text-xs text-tg-hint">
                        × {formatNumber(active.count)}
                        {extraBatches > 0 ? ` · +${extraBatches} ещё` : ""}
                      </div>
                    </div>
                    <div className="text-sm tabular-nums text-tg-text">
                      {formatDuration(remaining)}
                    </div>
                    <div className="ml-1 text-tg-hint">›</div>
                  </button>
                );
              }
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => setRecruiting(u)}
                  className="flex w-full items-center gap-3 rounded-xl bg-tg-secondaryBg p-3 text-left active:bg-tg-bg"
                >
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-tg-bg">
                    <UnitIcon unitId={u.id} cls={u.cls} size={28} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="truncate font-medium">{u.name}</div>
                      <div className="rounded bg-tg-bg px-1.5 py-0.5 text-xs text-tg-hint">
                        {UnitClassRu[u.cls]}
                      </div>
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-tg-hint">
                      <span>Hp {u.hp} · Atk {u.attack} · Def {u.defense} ·</span>
                      <CurrencyValue kind="gold" value={formatNumber(u.costPerUnit.gold)} />
                      {u.costPerUnit.wood ? (
                        <CurrencyValue kind="wood" value={formatNumber(u.costPerUnit.wood)} />
                      ) : null}
                      {u.costPerUnit.stone ? (
                        <CurrencyValue kind="stone" value={formatNumber(u.costPerUnit.stone)} />
                      ) : null}
                      {u.costPerUnit.mana ? (
                        <CurrencyValue kind="mana" value={formatNumber(u.costPerUnit.mana)} />
                      ) : null}
                      <span>· {formatDuration(u.timePerUnitSeconds)}</span>
                    </div>
                  </div>
                  <div className="text-tg-hint">›</div>
                </button>
              );
            })}
          </div>
          )}
        </Section>

        {hospitalKeys.length > 0 && (
          <Section title="Госпиталь" collapsible storageKey="hospital">
            <div className="space-y-2">
              {hospitalKeys.map((key) => {
                const { unitId, tier } = parseGarrisonKey(key);
                const u = unitsById.get(unitId);
                if (!u) return null;
                const poolCount = hospitalPool[key] ?? 0;
                const active = activeHealByKey.get(key);
                const tierBadge = tier > 1 ? `T${tier}` : null;
                if (active) {
                  // Already healing → progress row replaces the pool input.
                  // If pool still has wounded waiting, show them as a hint.
                  const remaining = secondsUntil(active.finishesAt, now);
                  return (
                    <div key={key} className="flex items-center gap-3 rounded-xl bg-tg-secondaryBg p-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-tg-bg">
                        <UnitIcon unitId={unitId} cls={u.cls} size={26} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="truncate text-sm font-medium">{u.name}</div>
                          {tierBadge && (
                            <div className="rounded bg-tg-bg px-1.5 py-0.5 text-[10px] tabular-nums text-tg-accent">
                              {tierBadge}
                            </div>
                          )}
                          <div className="rounded bg-tg-bg px-1.5 py-0.5 text-[10px] text-tg-accent">
                            Лечится
                          </div>
                        </div>
                        <div className="mt-0.5 text-xs text-tg-hint">
                          × {formatNumber(active.count)}
                          {poolCount > 0 ? ` · ещё ${formatNumber(poolCount)} в пуле` : ""}
                        </div>
                      </div>
                      <div className="text-sm tabular-nums text-tg-text">
                        <GiHealthPotion className="mr-1 inline" size={14} />
                        {formatDuration(remaining)}
                      </div>
                    </div>
                  );
                }
                return (
                  <HealPoolRow
                    key={key}
                    unit={u}
                    tier={tier}
                    count={poolCount}
                    available={data.state.resources}
                    ratio={HEAL_COST_RATIO}
                    pending={startHeal.isPending}
                    onConfirm={(n) => startHeal.mutate({ unitId, count: n, tier })}
                    confirmLabel={<><GiHealthPotion size={14} /> Лечить</>}
                  />
                );
              })}
            </div>
          </Section>
        )}

        {templeKeys.length > 0 && (
          <Section title="Храм возрождения" collapsible storageKey="temple">
            <div className="space-y-2">
              {templeKeys.map((key) => {
                const { unitId, tier } = parseGarrisonKey(key);
                const u = unitsById.get(unitId);
                if (!u) return null;
                const poolCount = templePool[key] ?? 0;
                const active = activeReviveByKey.get(key);
                const tierBadge = tier > 1 ? `T${tier}` : null;
                if (active) {
                  const remaining = secondsUntil(active.finishesAt, now);
                  return (
                    <div key={key} className="flex items-center gap-3 rounded-xl bg-tg-secondaryBg p-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-tg-bg">
                        <UnitIcon unitId={unitId} cls={u.cls} size={26} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="truncate text-sm font-medium">{u.name}</div>
                          {tierBadge && (
                            <div className="rounded bg-tg-bg px-1.5 py-0.5 text-[10px] tabular-nums text-tg-accent">
                              {tierBadge}
                            </div>
                          )}
                          <div className="rounded bg-tg-bg px-1.5 py-0.5 text-[10px] text-tg-accent">
                            Воскрешается
                          </div>
                        </div>
                        <div className="mt-0.5 text-xs text-tg-hint">
                          × {formatNumber(active.count)}
                          {poolCount > 0 ? ` · ещё ${formatNumber(poolCount)} в пуле` : ""}
                        </div>
                      </div>
                      <div className="text-sm tabular-nums text-tg-text">
                        <GiAnkh className="mr-1 inline" size={14} />
                        {formatDuration(remaining)}
                      </div>
                    </div>
                  );
                }
                return (
                  <HealPoolRow
                    key={key}
                    unit={u}
                    tier={tier}
                    count={poolCount}
                    available={data.state.resources}
                    ratio={REVIVE_COST_RATIO}
                    pending={startRevive.isPending}
                    onConfirm={(n) => startRevive.mutate({ unitId, count: n, tier })}
                    confirmLabel={<><GiAnkh size={14} /> Воскресить</>}
                  />
                );
              })}
            </div>
          </Section>
        )}

      </div>
      </>
      )}

      {recruiting && (() => {
        // Pick the training building level for this unit's class so the modal
        // can lock tiers that aren't reachable yet.
        const bt = buildingForClass[recruiting.cls];
        const trainingBuilding = bt != null
          ? data.state.buildings.find((b) => b.type === bt)
          : null;
        const buildingLevel = trainingBuilding?.level ?? 1;
        return (
          <RecruitModal
            unit={recruiting}
            resources={data.state.resources}
            trainSpeedReductionPct={trainSpeed}
            buildingLevel={buildingLevel}
            onClose={() => {
              setRecruiting(null);
              train.reset();
            }}
            onConfirm={(count, tier) =>
              train.mutate(
                { unitId: recruiting.id, count, tier },
                {
                  onSuccess: () => {
                    setRecruiting(null);
                    train.reset();
                  },
                },
              )
            }
            pending={train.isPending}
            error={errorMessage}
          />
        );
      })()}

      {promoting && (() => {
        const bt = buildingForClass[promoting.unit.cls];
        const buildingLevel = bt != null
          ? (data.state.buildings.find((b) => b.type === bt)?.level ?? 1)
          : 1;
        return (
          <PromoteModal
            unit={promoting.unit}
            fromTier={promoting.fromTier}
            available={promoting.available}
            resources={data.state.resources}
            trainSpeedReductionPct={trainSpeed}
            buildingLevel={buildingLevel}
            onClose={() => {
              setPromoting(null);
              promote.reset();
            }}
            onConfirm={(count, toTier) =>
              promote.mutate(
                { unitId: promoting.unit.id, fromTier: promoting.fromTier, toTier, count },
                {
                  onSuccess: () => {
                    setPromoting(null);
                    promote.reset();
                  },
                },
              )
            }
            pending={promote.isPending}
            error={promoteError}
          />
        );
      })()}

      {speedupTarget && (() => {
        const u = unitsById.get(speedupTarget.unitId);
        // Map class → training building. Same mapping used to filter the
        // recruit list (Infantry→Barracks etc.). Defaults to Barracks if the
        // unit catalog hasn't loaded yet, but the modal won't even open in
        // that case since the training order can't be on screen.
        const bt = (u && buildingForClass[u.cls]) ?? 6;
        return (
          <TrainingSpeedupModal
            order={speedupTarget}
            unit={u}
            buildingType={bt}
            inventory={data.state.inventory ?? {}}
            now={now}
            onClose={() => setSpeedupTarget(null)}
          />
        );
      })()}
    </div>
  );
}

function Section({
  title,
  children,
  collapsible = false,
  storageKey,
  defaultOpen = false,
}: {
  title: string;
  children: React.ReactNode;
  collapsible?: boolean;
  storageKey?: string;
  defaultOpen?: boolean;
}) {
  const lsKey = collapsible && storageKey ? `er.armyScreen.section.${storageKey}` : null;
  const [open, setOpen] = useState<boolean>(() => {
    if (!collapsible) return true;
    if (lsKey) {
      try {
        const v = localStorage.getItem(lsKey);
        if (v === "1") return true;
        if (v === "0") return false;
      } catch {
        // ignore — localStorage недоступен в SSR / приватном режиме
      }
    }
    return defaultOpen;
  });

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (lsKey) {
        try {
          localStorage.setItem(lsKey, next ? "1" : "0");
        } catch {
          // ignore
        }
      }
      return next;
    });
  };

  if (!collapsible) {
    return (
      <section>
        <div className="mb-1 text-xs uppercase tracking-wide text-tg-hint">{title}</div>
        {children}
      </section>
    );
  }

  return (
    <section>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="mb-1 flex w-full items-center gap-1.5 text-xs uppercase tracking-wide text-tg-hint active:text-tg-text"
      >
        <span
          className={`inline-block transition-transform ${open ? "rotate-90" : ""}`}
          aria-hidden
        >
          ›
        </span>
        <span>{title}</span>
      </button>
      {open ? children : null}
    </section>
  );
}

function Row({
  icon,
  title,
  subtitle,
  right,
  tierBadge,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  right?: string;
  // Tier label (e.g. "T1" / "T4") shown next to the title. Always shown in the
  // garrison so per-tier stacks read explicitly ("сколько T1, сколько T2").
  tierBadge?: string | null;
  // Optional trailing action (e.g. the «Повысить» button on garrison rows).
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-tg-secondaryBg p-2.5">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-tg-bg text-tg-text">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="truncate text-sm font-medium">{title}</div>
          {tierBadge ? (
            <span className="rounded bg-tg-bg px-1.5 py-0.5 text-[10px] tabular-nums text-tg-accent">
              {tierBadge}
            </span>
          ) : null}
        </div>
        {subtitle ? <div className="text-xs text-tg-hint">{subtitle}</div> : null}
      </div>
      {right ? <div className="text-sm tabular-nums text-tg-text">{right}</div> : null}
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <div className="rounded-xl bg-tg-secondaryBg p-3 text-sm text-tg-hint">{text}</div>;
}

// Row for the manual heal / revive pool. Player picks how many wounded of
// this unit to start treating, sees the cost preview, then taps the action.
// Cost = unit.costPerUnit × count × ratio (0.30 for heal, 0.70 for revive).
function HealPoolRow({
  unit,
  tier,
  count,
  available,
  ratio,
  pending,
  onConfirm,
  confirmLabel,
}: {
  unit: UnitCatalogEntry;
  // Tier of this pool row. Scales cost via tierCostMultiplier so higher
  // tiers cost proportionally more to heal / revive, matching the server.
  tier: number;
  count: number;
  available: { gold: number; wood: number; stone: number; mana: number };
  ratio: number;
  pending: boolean;
  onConfirm: (n: number) => void;
  // ReactNode so the caller can include a themed Gi icon alongside text.
  confirmLabel: React.ReactNode;
}) {
  const [picked, setPicked] = useState<number>(count);
  // Re-clamp when the pool size changes (e.g. after a previous heal succeeds).
  useEffect(() => {
    setPicked((p) => Math.min(Math.max(0, p), count));
  }, [count]);

  const safePicked = Math.max(0, Math.min(picked, count));
  const tierMult = tierCostMultiplier(tier);
  const cost = {
    gold:  Math.ceil(unit.costPerUnit.gold  * tierMult * safePicked * ratio),
    wood:  Math.ceil(unit.costPerUnit.wood  * tierMult * safePicked * ratio),
    stone: Math.ceil(unit.costPerUnit.stone * tierMult * safePicked * ratio),
    mana:  Math.ceil(unit.costPerUnit.mana  * tierMult * safePicked * ratio),
  };
  const canAfford =
    available.gold >= cost.gold &&
    available.wood >= cost.wood &&
    available.stone >= cost.stone &&
    available.mana >= cost.mana;
  const canSubmit = safePicked > 0 && canAfford && !pending;

  return (
    <div className="rounded-xl bg-tg-secondaryBg p-3">
      <div className="mb-2 flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-tg-bg">
          <UnitIcon unitId={unit.id} cls={unit.cls} size={26} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="truncate text-sm font-medium">{unit.name}</div>
            {tier > 1 && (
              <span className="rounded bg-tg-bg px-1.5 py-0.5 text-[10px] tabular-nums text-tg-accent">
                T{tier}
              </span>
            )}
          </div>
          <div className="text-xs text-tg-hint">
            В пуле: {formatNumber(count)}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setPicked(Math.floor(count / 2))}
            className="rounded bg-tg-bg px-2 py-1 text-[10px] text-tg-hint"
          >
            ½
          </button>
          <button
            type="button"
            onClick={() => setPicked(count)}
            className="rounded bg-tg-bg px-2 py-1 text-[10px] text-tg-hint"
          >
            всё
          </button>
        </div>
      </div>
      <input
        type="number"
        inputMode="numeric"
        min={0}
        max={count}
        value={safePicked > 0 ? safePicked : ""}
        placeholder={`0 / ${count}`}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") { setPicked(0); return; }
          const n = parseInt(raw, 10);
          if (!Number.isFinite(n)) return;
          setPicked(Math.max(0, Math.min(n, count)));
        }}
        className="w-full rounded-lg bg-tg-bg p-1.5 text-center tabular-nums"
      />
      {safePicked > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
          <span className="text-tg-hint">Стоит:</span>
          {cost.gold > 0 && <CurrencyValue kind="gold" value={formatNumber(cost.gold)} />}
          {cost.wood > 0 && <CurrencyValue kind="wood" value={formatNumber(cost.wood)} />}
          {cost.stone > 0 && <CurrencyValue kind="stone" value={formatNumber(cost.stone)} />}
          {cost.mana > 0 && <CurrencyValue kind="mana" value={formatNumber(cost.mana)} />}
        </div>
      )}
      <button
        type="button"
        disabled={!canSubmit}
        onClick={() => onConfirm(safePicked)}
        className="mt-2 w-full rounded-lg bg-tg-button px-3 py-2 text-sm font-medium text-tg-buttonText disabled:opacity-40"
      >
        {pending ? "Отправляю…" : (
          <span className="inline-flex items-center justify-center gap-1.5">
            {confirmLabel} <span className="tabular-nums">{formatNumber(safePicked)}</span>
          </span>
        )}
      </button>
      {!canAfford && safePicked > 0 && (
        <div className="mt-1 text-center text-[11px] text-tg-destructive">
          Недостаточно ресурсов
        </div>
      )}
    </div>
  );
}
